import * as z from "zod";
import { isoDate } from "./common";

export const NearbyShopProduct = z.object({
  id: z.string(),
  title: z.string(),
  handle: z.string(),
  imageUrl: z.string().nullable(),
  price: z.string().nullable(),
  currencyCode: z.string().nullable(),
  url: z.string(),
});
export type NearbyShopProduct = z.output<typeof NearbyShopProduct>;

export const NearbyShopLocation = z.object({
  name: z.string().nullable(),
  address1: z.string().nullable(),
  address2: z.string().nullable(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string().nullable(),
  countryCode: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
  formatted: z.string().nullable(),
});
export type NearbyShopLocation = z.output<typeof NearbyShopLocation>;

/** A map pin: one branch of one shop. `id` is the location id, `storeId` the shop. */
export const NearbyShop = z.object({
  id: z.string(),
  storeId: z.string(),
  locationId: z.string(),
  name: z.string(),
  locationName: z.string().nullable(),
  shopDomain: z.string().nullable(),
  url: z.string().nullable(),
  /** The shop's own blurb, shown on the map sheet. */
  description: z.string().nullable(),
  websiteUrl: z.string().nullable(),
  slug: z.string().nullable(),
  latitude: z.number(),
  longitude: z.number(),
  distanceKm: z.number(),
  /** The caller administers this shop. */
  isAdmin: z.boolean(),
  claimed: z.boolean(),
  canInviteOwner: z.boolean(),
  location: NearbyShopLocation,
  currencyCode: z.string().nullable(),
  products: z.array(NearbyShopProduct),
});
export type NearbyShop = z.output<typeof NearbyShop>;

export const ShopsNearby = z.object({
  shops: z.array(NearbyShop),
  /** The radius that ended up answering; the search widens from the one asked for. */
  radiusKm: z.number(),
  expanded: z.boolean(),
});
export type ShopsNearby = z.output<typeof ShopsNearby>;

export const ShopMapPin = z.object({
  id: z.string(),
  storeId: z.string(),
  slug: z.string().nullable(),
  name: z.string(),
  address1: z.string().nullable(),
  latitude: z.number(),
  longitude: z.number(),
  city: z.string().nullable(),
  province: z.string().nullable(),
  countryCode: z.string().nullable(),
  /** The shop's counter runs on HobbyScan: walk-ins scan cards to sell there, and pay there. */
  pointOfSale: z.boolean(),
  liveBreaks: z.boolean(),
  claimed: z.boolean(),
  canInviteOwner: z.boolean(),
});
export type ShopMapPin = z.output<typeof ShopMapPin>;

export const ShopMap = z.object({ pins: z.array(ShopMapPin) });
export type ShopMap = z.output<typeof ShopMap>;

export interface NearbyQuery {
  lat: number;
  lng: number;
  /**
   * Where the search *starts*, not where it stops: it widens from here until it
   * finds a few shops, so the answer's own `radiusKm` is the one to show a
   * customer — never the one asked for.
   */
  radiusKm?: number;
  limit?: number;
  /**
   * False takes `radiusKm` literally. Only worth setting for a viewport search,
   * where widening would answer "what is in this view" with the next country.
   */
  expand?: boolean;
  /**
   * False skips the per-shop product lookup, which is noticeably faster when all
   * that is being drawn is pins. The products it returns are cached for a few
   * minutes, so they are "what they carry", never live stock.
   */
  includeProducts?: boolean;
}

export const ShopPageLocation = z.object({
  name: z.string(),
  address: z.string().nullable(),
  phone: z.string().nullable(),
  openingHours: z.string().nullable(),
});
export type ShopPageLocation = z.output<typeof ShopPageLocation>;

/** The hosted shop page, as a visitor with no session sees it. */
export const PublicShopPage = z.object({
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  currencyCode: z.string().nullable(),
  contactEmail: z.string().nullable(),
  returnsPolicy: z.string().nullable(),
  locations: z.array(ShopPageLocation),
});
export type PublicShopPage = z.output<typeof PublicShopPage>;

/** The shop a customer is standing in, by the id their QR code carried. */
export const PublicShopLocation = z.object({
  id: z.string(),
  name: z.string(),
  city: z.string().nullable(),
  province: z.string().nullable(),
});
export type PublicShopLocation = z.output<typeof PublicShopLocation>;

export const PublicShopSummary = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string().nullable(),
  /** The counter a `loc_` id named; null for a store id. */
  locationId: z.string().nullable().default(null),
  /** Every counter a customer can walk into. */
  locations: z.array(PublicShopLocation).default([]),
});
export type PublicShopSummary = z.output<typeof PublicShopSummary>;

export const GeoRegion = z.object({
  code: z.string(),
  name: z.string(),
});
export type GeoRegion = z.output<typeof GeoRegion>;

/** A selectable country. `regions` is non-empty where a subdivision is required (US, CA, AU). */
export const GeoCountry = z.object({
  code: z.string(),
  name: z.string(),
  regions: z.array(GeoRegion),
});
export type GeoCountry = z.output<typeof GeoCountry>;

/** Coarse location from the caller's IP; every field null when nothing could be resolved. */
export const ApproximateLocation = z.object({
  available: z.boolean(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  city: z.string().nullable(),
  region: z.string().nullable(),
  countryCode: z.string().nullable(),
  source: z.string().nullable(),
});
export type ApproximateLocation = z.output<typeof ApproximateLocation>;

export const CLAIM_INVITATION_STATUSES = ["sent", "already_active", "cooldown"] as const;

export const ClaimInvitationRequested = z.object({
  status: z.enum(CLAIM_INVITATION_STATUSES),
  /** When another invitation may be requested; null unless `cooldown`. */
  retryAt: isoDate.nullable(),
});
export type ClaimInvitationRequested = z.output<typeof ClaimInvitationRequested>;

/** What an invitation link shows before sign-in. */
export const ClaimPreview = z.object({
  name: z.string().nullable(),
  description: z.string().nullable(),
  logoUrl: z.string().nullable(),
  websiteUrl: z.string().nullable(),
});
export type ClaimPreview = z.output<typeof ClaimPreview>;
